/* eslint max-lines: ["error", {max: 385}] -- applies to entire file */
/* eslint max-lines-per-function: ["error", {max: 315}] -- Due to useSelector, lookup spinner, lookup failure, and fresh-start flow */
/* eslint max-statements: ["error", {max: 36}] -- Due to useSelector, button title logic, lookup failure, fresh-start mutation, and feature flag */
/* eslint complexity: ["error", {max: 22}] -- Due to guard conditions, button title logic, and fresh-start branch */
import { useEffect, useMemo, useState } from "react";

import every from "lodash/every";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";

import {
  AnalyticsFieldName,
  TasksAnalyticsEvent,
  TasksAnalyticsSources,
} from "@/constants/analytics/Analytics";
import { ModalPath, TabPath } from "@/constants/Navigation";
import {
  TasksStatusId,
  TaskStatusLabel,
  TaskSubcategory,
} from "@/constants/Tasks";
import { TasksTestID, TextTestID } from "@/constants/TestID";
import { AnimationDuration } from "@/constants/UI";
import { router } from "@/crossPlatform/expo-router";
import useAssessmentLastUpdated from "@/hooks/useAssessmentLastUpdated";
import useEncodedNavigation from "@/hooks/useEncodedNavigation";
import useFeatureFlags from "@/hooks/useFeatureFlags";
import useTasks from "@/hooks/useTasks";
import { useToast } from "@/hooks/useToast";
import { useUnmount } from "@/hooks/useUnmount";
import { useAnalytics } from "@/services/Analytics";
import useAppMonitor from "@/services/AppMonitor";
import {
  useClearSurveyResultsMutation,
  useUpdateTaskMutation,
} from "@/store/api/tasks";
import { useAppSelector } from "@/store/hooks";
import {
  useAppUserProxyPatientsSelector,
  useAppUserSelector,
} from "@/store/reducers/appUser";
import {
  selectTaskDetailsData,
  useTaskDetailsModalDispatch,
} from "@/store/reducers/modals/taskDetailsModal";
import TaskBasicDetails from "@/ui/components/tasks/TaskBasicDetails";
import ModalFooter from "@/ui/composed/modal/ModalFooter";
import ModalTitleHeader from "@/ui/composed/modal/ModalTitleHeader";
import { ActionBar, FAIconName, FullScreenSpinner, Spinner } from "@/ui/core";
import { Box } from "@/ui/layout";
import ModalLayout from "@/ui/layout/ModalLayout";
import { isWeb } from "@/utils/platform.util";
import { isNotNilNotEmpty } from "@/utils/shared.utils";
import { joinTestIDs } from "@/utils/string.util";
import { isTaskEditable } from "@/utils/task.util";
import { getPreferredFirstName } from "@/utils/user.util";

const AssessmentTaskContent = {
  ButtonLabelResume: "Resume",
  ButtonLabelStartFresh: "Start fresh",
  ButtonLabelStartSurvey: "Start survey",
  EditorFallbackName: "a provider",
  ErrorLoadingAssessment: "Error loading assessment",
  ErrorStartingFresh: "Error starting fresh assessment",
  ErrorUpdatingTask: "Error updating task",
};

const baseTestID = TasksTestID.TaskDetails;

const Modal = () => {
  const { dismissWithEncoding } = useEncodedNavigation();
  const taskDetailsData = useAppSelector(selectTaskDetailsData);

  const {
    description,
    duration,
    isCore: isCoreFromSnapshot,
    lastUpdatedByUserId: snapshotLastUpdatedByUserId,
    lastUpdatedByUserType: snapshotLastUpdatedByUserType,
    linkedSurveyId,
    patientExternalId,
    patientName,
    selectedUserId,
    taskId,
    taskStatus,
    taskSubCategoryName,
    title,
  } = taskDetailsData || {};

  // Handle missing taskSubCategoryName for intake survey tasks.
  // API is not returning the taskSubCategoryName for intake survey tasks.
  const resolvedTaskSubCategory =
    taskSubCategoryName ||
    (title?.toLowerCase().includes("intake survey")
      ? TaskSubcategory.IntakeSurvey
      : undefined);

  const { captureException, captureMessage } = useAppMonitor();
  const { isAssignAssessmentsToProxySupportsEnabled } = useFeatureFlags();
  const { externalId: appUserId, isPatient, isSupport } = useAppUserSelector();
  const proxyPatients = useAppUserProxyPatientsSelector();

  const { resetTaskDetailsData, setTaskDetailsData } =
    useTaskDetailsModalDispatch();
  useUnmount({ onUnmount: resetTaskDetailsData });

  const { showToast } = useToast();
  const [clearSurveyResults] = useClearSurveyResultsMutation();
  const [updateTask] = useUpdateTaskMutation();
  const { track } = useAnalytics();
  // Need to set the task status to in progress locally when the webview is loaded so we don't have to fetch from the server
  const [localTaskStatus, setLocalTaskStatus] = useState<TaskStatusLabel>(
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- legitimate use case
    taskStatus as TaskStatusLabel,
  );
  const { tasks } = useTasks({
    appUserId,
    isPatient,
    linkedPatients: proxyPatients,
  });
  const {
    isCore,
    isLookupFailed,
    isLookupPending,
    lastUpdatedByUserId,
    lastUpdatedByUserType,
  } = useAssessmentLastUpdated({
    linkedSurveyId,
    selectedUserId,
    snapshot: {
      isCore: isCoreFromSnapshot,
      lastUpdatedByUserId: snapshotLastUpdatedByUserId,
      lastUpdatedByUserType: snapshotLastUpdatedByUserType,
    },
    tasks,
  });

  const editorName = useMemo(() => {
    if (isNil(selectedUserId)) {
      return AssessmentTaskContent.EditorFallbackName;
    }

    const selectedUserTasks = tasks[selectedUserId];
    if (isNil(selectedUserTasks)) {
      return AssessmentTaskContent.EditorFallbackName;
    }

    const lastUpdatedUser = selectedUserTasks
      .flatMap((task: TaskItem) => task.familyAssigneeUsers)
      .find(
        (user?: FamilyAssigneeUser) => user?.externalId === lastUpdatedByUserId,
      );

    if (isNil(lastUpdatedUser)) {
      return AssessmentTaskContent.EditorFallbackName;
    }

    return (
      getPreferredFirstName({
        chosenName: lastUpdatedUser.chosenName,
        firstName: lastUpdatedUser.firstName,
      }) || AssessmentTaskContent.EditorFallbackName
    );
  }, [tasks, selectedUserId, lastUpdatedByUserId]);

  const onDismiss = () => {
    // include patientExternalId in local params on web so we can select the correct carousel tab
    dismissWithEncoding({
      params: { selectedUserId: patientExternalId },
      pathname: TabPath.Home,
    });
  };

  useEffect(() => {
    if (!isLookupFailed) return;
    onDismiss();
    showToast({
      text: AssessmentTaskContent.ErrorLoadingAssessment,
      variant: "error",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once per failure
  }, [isLookupFailed]);

  if (
    isNil(title) ||
    isNil(description) ||
    isNil(duration) ||
    !isNotNilNotEmpty(taskId) ||
    !isNotNilNotEmpty(selectedUserId) ||
    isNil(taskStatus) ||
    isNil(resolvedTaskSubCategory) ||
    isEmpty(linkedSurveyId) ||
    !every([
      title,
      description,
      duration,
      taskId,
      selectedUserId,
      taskStatus,
      resolvedTaskSubCategory,
    ])
  ) {
    if (isEmpty(linkedSurveyId)) {
      captureMessage(`No linked survey ID for task: ${title} - ${taskId}`);
    }
    const text =
      "There was an issue getting the details for this assessment task";
    captureException(new Error(text));
    if (isWeb()) {
      onDismiss();
    }
    return <FullScreenSpinner />;
  }

  const performUpdateTask = async (taskStatusId: TasksStatusId) => {
    try {
      await updateTask({
        appUserId,
        isPatient,
        linkedPatients: proxyPatients || [],
        selectedUserId,
        taskId,
        taskStatusId,
      });

      router.navigate(ModalPath.AssessmentWebView);
      setTimeout(() => {
        setLocalTaskStatus(TaskStatusLabel.InProgress);
        // Update status label when the webview has been presented
      }, AnimationDuration.Default);
    } catch (error) {
      captureException(new Error("Error updating task", { cause: error }));

      // TODO: https://teamequip.atlassian.net/browse/JN-2305
      // Since toasts can't be displayed on modal screens, we need to dismiss the modal before showing the toast.
      // ( But also makes sense to dismiss the modal since if the update task request fails, the webview won't load
      // and the user would be stuck on the assessment task screen with no way to proceed )
      onDismiss();
      showToast({
        text: AssessmentTaskContent.ErrorUpdatingTask,
        variant: "error",
      });
    }
  };

  const performClearAndStartFresh = async () => {
    if (!linkedSurveyId || !taskDetailsData) return;
    try {
      await clearSurveyResults({ linkedSurveyId }).unwrap();
      setTaskDetailsData({
        ...taskDetailsData,
        lastUpdatedByUserId: appUserId,
      });
      router.navigate(ModalPath.AssessmentWebView);
    } catch (error) {
      captureException(
        new Error("Error clearing survey results", { cause: error }),
      );
      onDismiss();
      showToast({
        text: AssessmentTaskContent.ErrorStartingFresh,
        variant: "error",
      });
    }
  };

  const handleStartAssessment = () => {
    const event =
      localTaskStatus === TaskStatusLabel.ToDo
        ? TasksAnalyticsEvent.StartSurvey
        : TasksAnalyticsEvent.ResumeSurvey;
    track(event, {
      [AnalyticsFieldName.Component]: ModalPath.AssessmentTask,
      [AnalyticsFieldName.Source]: TasksAnalyticsSources.Section,
      [AnalyticsFieldName.Title]: title,
    });

    if (isFreshStart) {
      void performClearAndStartFresh();
      return;
    }

    const isTaskInProgress =
      taskStatus === TaskStatusLabel.InProgress ||
      localTaskStatus === TaskStatusLabel.InProgress;
    if (isTaskInProgress) {
      router.navigate(ModalPath.AssessmentWebView);
    } else {
      void performUpdateTask(TasksStatusId.InProgress);
    }
  };

  const { isEditable: isAssessmentEditable, isFreshStart } = isTaskEditable({
    currentUserId: appUserId,
    isAssignAssessmentsToProxySupportsEnabled,
    isCore,
    isSupport,
    lastUpdatedByUserId,
    lastUpdatedByUserType,
    taskStatus,
    taskSubCategoryName: resolvedTaskSubCategory,
  });

  const getButtonTitle = () => {
    if (isFreshStart) return AssessmentTaskContent.ButtonLabelStartFresh;
    if (localTaskStatus === TaskStatusLabel.ToDo)
      return AssessmentTaskContent.ButtonLabelStartSurvey;
    return AssessmentTaskContent.ButtonLabelResume;
  };
  const buttonTitle = getButtonTitle();

  return (
    <ModalLayout
      isPaddedHorizontally
      renderFooter={() => {
        if (isLookupPending) {
          return (
            <Box
              marginTop={12}
              testID={joinTestIDs(baseTestID, "lookup-spinner")}
            >
              <Spinner />
            </Box>
          );
        }
        if (!isAssessmentEditable) return;
        return (
          <ModalFooter
            leftButtonType="cancel"
            onLeftButtonPress={onDismiss}
            onRightButtonPress={handleStartAssessment}
            rightButtonLabel={buttonTitle}
            rightButtonRightIcon={FAIconName.ArrowRight}
            testID={baseTestID}
          />
        );
      }}
      renderHeader={() => {
        return (
          <ModalTitleHeader
            navigationType="close"
            onDismiss={onDismiss}
            testID={joinTestIDs(baseTestID, TextTestID.Title)}
            title="To do"
          />
        );
      }}
    >
      <TaskBasicDetails
        description={description}
        duration={duration}
        patientName={patientName}
        testID={baseTestID}
        title={title}
      />

      {!isLookupPending && !isAssessmentEditable && (
        <Box marginTop={12}>
          <ActionBar
            iconName={FAIconName.InfoCircle}
            message={`This to-do has already been started by ${editorName}`}
            size="small"
            testID={joinTestIDs(baseTestID, "action-bar")}
            variant="warning"
          />
        </Box>
      )}
      {!isLookupPending && isAssessmentEditable && isFreshStart && (
        <Box marginTop={12}>
          <ActionBar
            iconName={FAIconName.InfoCircle}
            message={`Started by ${editorName} — opening will start fresh`}
            size="small"
            testID={joinTestIDs(baseTestID, "fresh-start-bar")}
            variant="info"
          />
        </Box>
      )}
    </ModalLayout>
  );
};

export { AssessmentTaskContent };
export default Modal;
